import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Policy } from '@mui/icons-material';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import {
  useCourtAdministrativeUnitAssignments,
  useCourts,
  useFederalStates,
} from '../../Aemter/hooks';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { useGetLandRegisterAccesses } from '../../hooks/reactQuerys/FederalStateLoginQueries';
import { useSearchLandRegister } from '../../hooks/reactQuerys/LandMarkingQueries';
import { fetchBorisData } from '../../hooks/reactQuerys/RealEstateQueries';
import {
  ISearchOptionLandRegister,
  searchOptionLandRegisterSchema,
} from '../../Interfaces/Helper/ISearchOptionLandRegister';
import { ILegalTransactionRelationshipRealEstate } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { useAlert } from '../../context/AlertContext';
import { customFetcher } from '../../hooks/useFetch';
import { RealEstateMap } from '../ProcessManager/RealEstateMap';
import { BlattAnlageAusRecherche } from './BlattAnlageAusRecherche';
import { aktenzeichenFromLegalTransaction } from './aktenzeichen';
import { applyBorisResult } from './borisPrefill';
import { ILegalTransactionGrundbuchblattLink } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';

interface IProps {
  realEstateObject: ILegalTransactionRelationshipRealEstate;
  setRealEstateObject: Function;
  /** Optional: ohne Vorgang (z. B. aus der Übersicht „Grundstücke") nutzbar. */
  legalTransaction?: ILegalTransactionFullObject;
  /** Dialog direkt beim Mounten öffnen (Einsatz als Schritt in einem Flow). */
  autoOpen?: boolean;
  /** Eigenen Trigger (Policy-Icon) ausblenden, wenn extern gesteuert. */
  hideTrigger?: boolean;
  /** Wird gerufen, wenn der Recherche-Dialog geschlossen wird (ohne Übernahme). */
  onClosed?: () => void;
  /**
   * Vorgang-Kontext: aus der Recherche angelegte und „zuzuordnende" Blätter werden
   * hierüber gemeldet. Der Aufrufer nimmt sie in die Vorgang-GrundbuchblattArray auf
   * (Verknüpfung erst beim Speichern des Vorgangs). Fehlt der Callback, ist die
   * Zuordnung deaktiviert (reine Anlage, z. B. aus der Grundstücke-Übersicht).
   */
  onBlaetterAssigned?: (links: ILegalTransactionGrundbuchblattLink[]) => void;
}

/** Rohzeile aus /landregister/search. idAdministrativeUnit kommt als Objekt. */
interface IRawRow {
  LastName?: string | null;
  FirstName?: string | null;
  Birthname?: string | null;
  Birthday?: string | null;
  SheetLandRegister?: string | null;
  Parcel?: string | null;
  LandParcel?: string | null;
  StreetSearch?: string | null;
  idCourt?: number | null;
  idAdministrativeUnit?:
    | number
    | {
        idAdministrativeUnit?: number;
        idCourt?: number;
        AdministrativeUnit?: string;
        Court?: string;
      }
    | null;
}

interface IOwner {
  LastName: string;
  FirstName: string;
  Birthday: string;
}

/** Ein zusammengefasster Grundbucheintrag (je Blatt+Flur+Flurstück). */
interface IGroupedEntry {
  key: string;
  SheetLandRegister: string;
  Parcel: string; // Flur
  LandParcel: string; // Flurstück
  StreetSearch: string; // Lage
  gemarkungName: string;
  courtName: string; // Amtsgericht-Ort (Geocoding-Locality)
  idAdministrativeUnit: number | null;
  idCourt: number | null;
  owners: IOwner[];
  ownerSetKey: string;
}

const OWNER_COLORS = [
  '#1976d2',
  '#d32f2f',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#0097a7',
  '#c2185b',
  '#5d4037',
];

const clean = (s: unknown): string => (s ?? '').toString().trim();

const isBrandenburg = (name?: string | null) =>
  (name ?? '').toLowerCase().startsWith('brandenburg');

const rowAdminUnit = (r: IRawRow) =>
  typeof r.idAdministrativeUnit === 'object' && r.idAdministrativeUnit !== null
    ? r.idAdministrativeUnit
    : null;

const ownerLabel = (o: IOwner) =>
  `${o.LastName} ${o.FirstName}`.trim() +
  (o.Birthday ? ` (* ${o.Birthday})` : '');

/**
 * Eigentümerrecherche (Grundbuch): ermittelt Grundbucheinträge statt sie
 * vorauszusetzen. Bundesland aus Adresse vorbelegt; in Brandenburg bestimmt
 * BORIS die Gemarkung; Name aus den Eigentümern des Grundstücks. Treffer werden
 * je Blatt+Flurstück zusammengefasst (Eigentümer aggregiert) und können
 * mehrfach als Grundbuchinformationen übernommen werden. In Brandenburg zeigt
 * eine Karte die Treffer (gleiche Eigentümer gleich eingefärbt); ist die Lage
 * nicht geocodierbar, wird über Flur/Flurstück verortet.
 */
export const EigentuemerRechercheDialog: React.FC<IProps> = ({
  realEstateObject,
  legalTransaction,
  autoOpen,
  hideTrigger,
  onClosed,
  onBlaetterAssigned,
}) => {
  const { showAlert } = useAlert();
  const { federalStates } = useFederalStates();
  const { courts } = useCourts();
  const { courtAdministrativeUnitAssignments: adminUnits } =
    useCourtAdministrativeUnitAssignments();
  const { landRegisterAccesses } = useGetLandRegisterAccesses();
  const { searchLandRegister, isSearchingLandRegister } =
    useSearchLandRegister();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'form' | 'results'>('form');
  // Ergebnis-Ansicht: Trefferliste oder blatt-zentrische Anlage (nach Blattnummer).
  const [view, setView] = useState<'table' | 'create'>('table');
  const [search, setSearch] = useState<ISearchOptionLandRegister>(
    searchOptionLandRegisterSchema.parse({})
  );
  const [rows, setRows] = useState<IRawRow[]>([]);
  const [coords, setCoords] = useState<
    Record<string, { lat: number; lng: number }>
  >({});
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [borisHint, setBorisHint] = useState<string | null>(null);
  // Passwort wird erst beim Klick auf „Suchen" abgefragt (nicht gespeichert).
  const [pwPrompt, setPwPrompt] = useState<{ open: boolean; password: string }>(
    {
      open: false,
      password: '',
    }
  );

  const selectedFederalStateName = federalStates?.find(
    f => f.idFederalState === search.idFederalState
  )?.FederalState;
  const brandenburg = isBrandenburg(selectedFederalStateName);

  const addressString = useMemo(() => {
    const line1 = [realEstateObject.Street, realEstateObject.StreetNr]
      .filter(Boolean)
      .join(' ')
      .trim();
    const line2 = [realEstateObject.Postcode, realEstateObject.City]
      .filter(Boolean)
      .join(' ')
      .trim();
    return [line1, line2].filter(Boolean).join(', ').trim();
  }, [realEstateObject]);

  const accessesForState = (landRegisterAccesses ?? []).filter(
    a => a.idFederalState === search.idFederalState
  );

  const selectedFederalStateNameFor = (id: number | null) =>
    federalStates?.find(f => f.idFederalState === id)?.FederalState;

  // Treffer je Blatt+Flur+Flurstück zusammenfassen, Eigentümer aggregieren
  const grouped: IGroupedEntry[] = useMemo(() => {
    const map = new Map<string, IGroupedEntry>();
    rows.forEach(r => {
      const blatt = clean(r.SheetLandRegister);
      const flur = clean(r.Parcel);
      const flurstueck = clean(r.LandParcel);
      const lage = clean(r.StreetSearch);
      const key = `${blatt}|${flur}|${flurstueck}`;
      const au = rowAdminUnit(r);
      let entry = map.get(key);
      if (!entry) {
        entry = {
          key,
          SheetLandRegister: blatt,
          Parcel: flur,
          LandParcel: flurstueck,
          StreetSearch: lage,
          gemarkungName: clean(au?.AdministrativeUnit),
          courtName: clean(au?.Court),
          idAdministrativeUnit:
            au?.idAdministrativeUnit ??
            (typeof r.idAdministrativeUnit === 'number'
              ? r.idAdministrativeUnit
              : null),
          idCourt: au?.idCourt ?? r.idCourt ?? null,
          owners: [],
          ownerSetKey: '',
        };
        map.set(key, entry);
      }
      const owner: IOwner = {
        LastName: clean(r.LastName),
        FirstName: clean(r.FirstName),
        Birthday: clean(r.Birthday),
      };
      const oKey = `${owner.LastName}|${owner.FirstName}|${owner.Birthday}`;
      if (
        !entry.owners.some(
          o => `${o.LastName}|${o.FirstName}|${o.Birthday}` === oKey
        )
      )
        entry.owners.push(owner);
    });
    // ownerSetKey (für Einfärbung gleicher Eigentümer)
    const result = Array.from(map.values());
    result.forEach(e => {
      e.ownerSetKey = e.owners
        .map(o => `${o.LastName}|${o.FirstName}|${o.Birthday}`)
        .sort()
        .join('#');
    });
    return result;
  }, [rows]);

  // Farbe je Eigentümer-Set (gleiche Eigentümer -> gleiche Farbe)
  const ownerSetColor = useMemo(() => {
    const map: Record<string, string> = {};
    let i = 0;
    grouped.forEach(g => {
      if (!(g.ownerSetKey in map)) {
        map[g.ownerSetKey] = OWNER_COLORS[i % OWNER_COLORS.length];
        i++;
      }
    });
    return map;
  }, [grouped]);

  // BORIS-Vorbelegung der Gemarkung aus der Adresse (Brandenburg)
  const runBorisPrefill = async () => {
    if (!addressString) return;
    setBorisHint('BORIS ermittelt die Gemarkung …');
    try {
      const res = await fetchBorisData(addressString);
      const fs = res.flurstueck;
      const gemarkung = fs?.gemarkung ?? undefined;
      const flur = fs?.flur ?? undefined;
      const flurstueck = fs?.flurstuecksnummer ?? undefined;
      // Auf den AKTUELLEN Stand anwenden, nicht auf den Snapshot von vor der
      // Abfrage — sonst verschwindet, was der Mitarbeiter waehrenddessen
      // eingetippt hat (zuerst das Pflichtfeld Aktenzeichen).
      setSearch((prev: ISearchOptionLandRegister) =>
        applyBorisResult(
          prev,
          { gemarkung, flur, flurstueck },
          adminUnits ?? []
        )
      );
      setBorisHint(
        gemarkung
          ? `BORIS: Gemarkung „${gemarkung}"${flur ? `, Flur ${flur}` : ''}${
              flurstueck ? `, Flurstück ${flurstueck}` : ''
            }`
          : 'BORIS konnte keine Gemarkung bestimmen.'
      );
    } catch {
      setBorisHint('BORIS-Abfrage fehlgeschlagen.');
    }
  };

  const handleOpen = async () => {
    // Aus einem Vorgang heraus vorbelegt (Urkundennummer, sonst
    // Vorgangsnummer); ohne Vorgang bleibt es leer und der Mitarbeiter traegt
    // es in der Maske ein — das Portal fuehrt es als Pflichtfeld.
    const searchCaseNumber = aktenzeichenFromLegalTransaction(legalTransaction);

    const firstEntry = realEstateObject.LandParcelEntryArray[0];
    const idCourt = realEstateObject.idCourt ?? firstEntry?.idCourt ?? null;
    const court = courts?.find(c => c.idCourt === idCourt);
    const idFederalState = court?.idFederalState ?? null;

    // Name aus den Eigentümern des Grundstücks vorbelegen
    let searchTerm = '';
    const firstOwnerId = (realEstateObject.OwnerArray ?? [])[0]?.idLegalClient;
    if (firstOwnerId && firstOwnerId > 0) {
      try {
        const owner = await customFetcher({
          endpoint: `/legalclient/fullObject/${firstOwnerId}`,
        });
        if (owner && typeof owner === 'object' && 'LastName' in owner) {
          searchTerm = String((owner as { LastName?: string }).LastName ?? '');
        }
      } catch {
        /* Name bleibt leer */
      }
    }

    const stateAccesses = (landRegisterAccesses ?? []).filter(
      a => a.idFederalState === idFederalState
    );

    const next = searchOptionLandRegisterSchema.parse({
      SearchCaseNumber: searchCaseNumber,
      idFederalState,
      idCourt,
      idAdministrativeUnit: firstEntry?.idAdministrativeUnit ?? null,
      idFederalStateLogin:
        stateAccesses.length === 1
          ? stateAccesses[0].idFederalStateLogin
          : null,
      SearchTerm: searchTerm,
    });

    setSearch(next);
    setRows([]);
    setCoords({});
    setBorisHint(null);
    setStep('form');
    setView('table');
    setOpen(true);

    const fsName = federalStates?.find(
      f => f.idFederalState === idFederalState
    )?.FederalState;
    if (isBrandenburg(fsName)) void runBorisPrefill();
  };

  // Als Schritt in einem Flow: Dialog automatisch öffnen.
  const autoOpenedRef = useRef(false);
  const handleOpenRef = useRef(handleOpen);
  handleOpenRef.current = handleOpen;
  useEffect(() => {
    if (autoOpen && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      void handleOpenRef.current();
    }
  }, [autoOpen]);

  const formValid =
    !!search.idFederalStateLogin &&
    search.idAdministrativeUnit !== null &&
    search.SearchTerm !== '' &&
    search.SearchCaseNumber.trim() !== '';

  const onSearch = async (password: string) => {
    try {
      const uploadObject = searchOptionLandRegisterSchema.parse({
        ...search,
        Password: password,
      });
      const localResults = await searchLandRegister(uploadObject);
      const raw = (localResults ?? []) as unknown as IRawRow[];
      setRows(raw);
      setStep('results');
      setView('table');
    } catch {
      showAlert({
        severity: 'error',
        text: 'Die Eigentümerrecherche konnte nicht durchgeführt werden. Bitte prüfen Sie Zugang, Passwort und die Verfügbarkeit des Grundbuchs.',
      });
    }
  };

  const startSearchWithPassword = () => {
    const pw = pwPrompt.password;
    setPwPrompt({ open: false, password: '' });
    void onSearch(pw);
  };

  // Treffer verorten — direkt über OpenStreetMap/Nominatim (clientseitig).
  // Adresse + Amtsgerichts-Ort liefert hausnummerngenaue Punkte; der Ortskern
  // ist nur der letzte Fallback. (BORIS-LGB lieferte für Straßen nur den Ort.)
  React.useEffect(() => {
    if (step !== 'results' || !brandenburg || grouped.length === 0) return;
    let cancelled = false;
    const provider = new OpenStreetMapProvider({
      params: { countrycodes: 'de', addressdetails: 0 },
    });
    const geocode = async (query: string) => {
      try {
        const res = await provider.search({ query });
        return res[0] ? { lat: Number(res[0].y), lng: Number(res[0].x) } : null;
      } catch {
        return null;
      }
    };
    (async () => {
      setIsGeocoding(true);
      const next: Record<string, { lat: number; lng: number }> = {};
      for (const g of grouped) {
        let c: { lat: number; lng: number } | null = null;
        const ort = g.courtName || g.gemarkungName;
        const tries: string[] = [];
        if (g.StreetSearch && ort) tries.push(`${g.StreetSearch}, ${ort}`);
        if (g.StreetSearch && g.gemarkungName)
          tries.push(`${g.StreetSearch}, ${g.gemarkungName}`);
        if (ort) tries.push(`${ort}, Brandenburg`);
        for (const q of tries) {
          c = await geocode(q);
          if (c) break;
        }
        if (c) next[g.key] = c;
      }
      if (!cancelled) {
        setCoords(next);
        setIsGeocoding(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, brandenburg, grouped.length]);

  const markers = useMemo(
    () =>
      grouped
        .filter(g => coords[g.key])
        .map(g => ({
          lat: coords[g.key].lat,
          lng: coords[g.key].lng,
          color: ownerSetColor[g.ownerSetKey],
          label: `${g.owners.map(ownerLabel).join(', ')} · ${
            g.StreetSearch || `Flur ${g.Parcel}/${g.LandParcel}`
          } · Blatt ${g.SheetLandRegister}`,
        })),
    [grouped, coords, ownerSetColor]
  );

  return (
    <>
      {!hideTrigger && (
        <Tooltip title='Eigentümerrecherche'>
          <IconButton onClick={handleOpen}>
            <Policy />
          </IconButton>
        </Tooltip>
      )}

      {isSearchingLandRegister && (
        <Dialog open>
          <DialogContent>
            <Typography>
              Das Grundbuch wird durchsucht. <br />
              Dies kann mehrere Minuten dauern, bitte haben Sie Geduld!
            </Typography>
            <CircularProgress />
          </DialogContent>
        </Dialog>
      )}

      {/* Passwort wird erst beim Suchen abgefragt (nicht gespeichert) */}
      <Dialog
        open={pwPrompt.open}
        onClose={() => setPwPrompt({ open: false, password: '' })}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Passwort des Zugangs</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Bitte geben Sie das Passwort Ihres Zugangs ein. Es wird nur für
            diese Suche verwendet und nicht gespeichert.
          </Typography>
          <TextField
            type='password'
            label='Passwort'
            fullWidth
            autoFocus
            value={pwPrompt.password}
            onChange={e =>
              setPwPrompt(p => ({ ...p, password: e.target.value }))
            }
            onKeyDown={e => {
              if (e.key === 'Enter' && pwPrompt.password)
                startSearchWithPassword();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => setPwPrompt({ open: false, password: '' })}
          >
            Abbruch
          </Button>
          <Button
            variant='contained'
            disabled={!pwPrompt.password}
            onClick={startSearchWithPassword}
          >
            Suche starten
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          onClosed?.();
        }}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>Eigentümerrecherche (Grundbuch)</DialogTitle>
        <DialogContent dividers>
          {step === 'form' ? (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <CustomeTextField
                  label='Aktenzeichen'
                  attr='SearchCaseNumber'
                  object={search}
                  setObject={setSearch}
                  type='string'
                  required
                />
                <Typography variant='caption' color='text.secondary'>
                  {legalTransaction
                    ? 'Aus dem Vorgang übernommen — das Grundbuchportal führt das Aktenzeichen in seinem Abrufprotokoll.'
                    : 'Pflichtfeld des Grundbuchportals. Ohne Vorgang bitte selbst angeben, z. B. die Urkundennummer.'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <CustomeTextField
                  label='Bundesland'
                  attr='idFederalState'
                  object={search}
                  setObject={(u: ISearchOptionLandRegister) => {
                    const stateAccesses = (landRegisterAccesses ?? []).filter(
                      a => a.idFederalState === u.idFederalState
                    );
                    const updated = {
                      ...u,
                      idFederalStateLogin:
                        stateAccesses.length === 1
                          ? stateAccesses[0].idFederalStateLogin
                          : null,
                    };
                    setSearch(updated);
                    if (
                      isBrandenburg(
                        selectedFederalStateNameFor(u.idFederalState)
                      )
                    )
                      void runBorisPrefill();
                  }}
                  type='select'
                  null={search.idFederalState === null}
                >
                  {(federalStates ?? [])
                    .filter(f => f.isEnabledSolumWeb)
                    .map(f => (
                      <MenuItem
                        key={`fs-${f.idFederalState}`}
                        value={f.idFederalState}
                      >
                        {f.FederalState}
                      </MenuItem>
                    ))}
                </CustomeTextField>
              </Grid>
              <Grid item xs={6}>
                <CustomeTextField
                  label='Zugang'
                  attr='idFederalStateLogin'
                  object={search}
                  setObject={setSearch}
                  type='select'
                  null={search.idFederalStateLogin === null}
                >
                  {accessesForState.map(a => (
                    <MenuItem
                      key={`acc-${a.idFederalStateLogin}`}
                      value={a.idFederalStateLogin}
                    >
                      {a.Username}
                    </MenuItem>
                  ))}
                </CustomeTextField>
              </Grid>
              <Grid item xs={6}>
                <CustomeTextField
                  label='Gericht'
                  attr='idCourt'
                  object={search}
                  setObject={(u: ISearchOptionLandRegister) =>
                    setSearch({ ...u, idAdministrativeUnit: null })
                  }
                  type='select'
                  null={search.idCourt === null}
                >
                  {(courts ?? [])
                    .filter(c => c.idFederalState === search.idFederalState)
                    .map(c => (
                      <MenuItem key={`court-${c.idCourt}`} value={c.idCourt}>
                        {c.Court}
                      </MenuItem>
                    ))}
                </CustomeTextField>
              </Grid>
              <Grid item xs={6}>
                <CustomeTextField
                  label='Gemarkung'
                  attr='idAdministrativeUnit'
                  object={search}
                  setObject={(u: ISearchOptionLandRegister) => {
                    const au = (adminUnits ?? []).find(
                      a => a.idAdministrativeUnit === u.idAdministrativeUnit
                    );
                    setSearch({ ...u, idCourt: au?.idCourt ?? u.idCourt });
                  }}
                  type='select'
                  null={search.idAdministrativeUnit === null}
                >
                  {(adminUnits ?? [])
                    .filter(
                      a =>
                        a.idCourt === search.idCourt || search.idCourt === null
                    )
                    .sort((a, b) =>
                      String(a.AdministrativeUnit) <
                      String(b.AdministrativeUnit)
                        ? -1
                        : 1
                    )
                    .map(a => (
                      <MenuItem
                        key={`au-${a.idAdministrativeUnit}`}
                        value={a.idAdministrativeUnit}
                      >
                        {search.idCourt === null
                          ? `${a.AdministrativeUnit}, ${a.Court}`
                          : a.AdministrativeUnit}
                      </MenuItem>
                    ))}
                </CustomeTextField>
              </Grid>
              <Grid item xs={12}>
                <CustomeTextField
                  label='Name (Inhaber)'
                  attr='SearchTerm'
                  object={search}
                  setObject={setSearch}
                  type='string'
                />
              </Grid>
              {brandenburg && (
                <Grid item xs={12}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() => void runBorisPrefill()}
                      disabled={!addressString}
                    >
                      Gemarkung über BORIS bestimmen
                    </Button>
                    {borisHint && (
                      <Typography variant='caption' color='text.secondary'>
                        {borisHint}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              )}
            </Grid>
          ) : view === 'create' ? (
            <BlattAnlageAusRecherche
              entries={grouped}
              idFederalStateLogin={search.idFederalStateLogin}
              searchCaseNumber={search.SearchCaseNumber}
              onAssigned={
                onBlaetterAssigned
                  ? links => {
                      onBlaetterAssigned(links);
                      setOpen(false);
                      onClosed?.();
                    }
                  : undefined
              }
            />
          ) : (
            <Stack spacing={2}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Lage</TableCell>
                    <TableCell>Gemarkung</TableCell>
                    <TableCell>Flur</TableCell>
                    <TableCell>Flurstück</TableCell>
                    <TableCell>Blattnr.</TableCell>
                    <TableCell>Eigentümer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grouped.map(g => (
                    <TableRow key={g.key} hover>
                      <TableCell>{g.StreetSearch || '–'}</TableCell>
                      <TableCell>{g.gemarkungName || '–'}</TableCell>
                      <TableCell>{g.Parcel || '–'}</TableCell>
                      <TableCell>{g.LandParcel || '–'}</TableCell>
                      <TableCell>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          {brandenburg && ownerSetColor[g.ownerSetKey] && (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: ownerSetColor[g.ownerSetKey],
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span>{g.SheetLandRegister || '–'}</span>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          {g.owners.map((o, i) => (
                            <Typography key={`o-${g.key}-${i}`} variant='body2'>
                              {ownerLabel(o)}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {grouped.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant='body2' color='text.secondary'>
                          Keine Treffer.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {brandenburg && (
                <Card variant='outlined'>
                  <CardContent>
                    <Typography
                      variant='subtitle1'
                      fontWeight={600}
                      sx={{ mb: 1 }}
                    >
                      Lage der Treffer (BORIS)
                      {isGeocoding && (
                        <CircularProgress size={14} sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {markers.length > 0 ? (
                      <RealEstateMap
                        lat={markers[0].lat}
                        lng={markers[0].lng}
                        related={markers}
                        hideCenter
                      />
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        {isGeocoding
                          ? 'Treffer werden verortet (Lage bzw. Flur/Flurstück) …'
                          : 'Keine verortbaren Treffer.'}
                      </Typography>
                    )}
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      Gleiche Farbe = gleiche Eigentümer. Ließ sich die Adresse
                      nicht verorten, wird über Flur/Flurstück (ALKIS) markiert.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setOpen(false)}>
            Schließen
          </Button>
          {step === 'form' ? (
            <Button
              variant='contained'
              onClick={() => setPwPrompt({ open: true, password: '' })}
              disabled={!formValid}
            >
              Suchen
            </Button>
          ) : view === 'create' ? (
            <Button variant='outlined' onClick={() => setView('table')}>
              Zurück zur Liste
            </Button>
          ) : (
            <>
              <Button variant='outlined' onClick={() => setStep('form')}>
                Neue Suche
              </Button>
              <Button
                variant='contained'
                onClick={() => setView('create')}
                disabled={grouped.length === 0}
              >
                Blätter anlegen (Grundbuch)
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
